import { styled } from "@monolith-forensics/monolith-ui";

import { isAssignedToCase } from "../contacts.helpers";
import { Contact } from "../contacts.types";

/**
 * Stacked rather than columnar: at 380px there is no room for the name, email
 * and role to sit side by side the way `renderUser` lays them out.
 */
const NarrowUserRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .primary {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .secondary {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/**
 * Lays the name stack against the row's status: details take the slack, the
 * status sits hard right, both centred on the row's 60px.
 */
const ContactRowLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  /* Without this the stack's own min-width: 0 is overridden by this flex item
     refusing to shrink, and a long name pushes the status off the row instead
     of ellipsing. */
  min-width: 0;

  ${NarrowUserRow} {
    flex: 1 1 auto;
  }
`;

/*
  Marked rather than hidden: a contact missing from the picker with no
  explanation reads as a search problem. Saying why costs one line and leaves
  the record counts honest.

  Kept at full opacity while the details beside it dim — it is the reason the
  row is unavailable, so it is the one part that must stay easy to read.
*/
const AssignedNote = styled.div`
  flex: 0 0 auto;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  /* flex: 0 0 auto already keeps it at content width; nowrap makes sure a
     narrower list breaks the name's ellipsis rather than this onto two lines. */
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const ContactItem: React.FC<{ contact: Contact }> = ({ contact }) => {
  const subtitle =
    [contact.email, contact.organization].filter(Boolean).join(" • ") || "—";
  const assigned = isAssignedToCase(contact);

  return (
    <ContactRowLayout>
      <NarrowUserRow style={assigned ? { opacity: 0.55 } : undefined}>
        <div className="primary">{contact.name}</div>
        <div className="secondary">{subtitle}</div>
      </NarrowUserRow>
      {assigned && <AssignedNote>Assigned</AssignedNote>}
    </ContactRowLayout>
  );
};
